import React, { useState, useEffect } from 'react';
import axios from 'axios';
import './SubscribersList.css';

const SubscribersList = () => {
  const [subscribers, setSubscribers] = useState([]);
  const [filteredSubscribers, setFilteredSubscribers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [locations, setLocations] = useState([]);
  const [packs, setPacks] = useState([]);

  // États pour les filtres
  const [filters, setFilters] = useState({
    location: '',
    packType: '',
    startDate: '',
    endDate: '',
    searchTerm: ''
  });

  // Charger les données
  useEffect(() => {
    fetchSubscribers();
    fetchLocations();
    fetchPacks();
  }, []);

  // Appliquer les filtres quand ils changent
  useEffect(() => {
    applyFilters();
  }, [filters, subscribers]);

  const fetchSubscribers = async () => {
    setLoading(true);
    try {
      const response = await axios.get('/api/event/getAll');
      setSubscribers(response.data);
    } catch (error) {
      console.error('Erreur lors du chargement des abonnés:', error);
      alert('❌ Impossible de charger la liste des abonnés');
    } finally {
      setLoading(false);
    }
  };

  const fetchLocations = async () => {
    try {
      const response = await axios.get('/api/Locations/getAllLocations');
      setLocations(response.data);
    } catch (error) {
      console.error('Erreur lors du chargement des localisations:', error);
    }
  };

  const fetchPacks = async () => {
    try {
      const response = await axios.get('/api/pack/getAll');
      setPacks(response.data);
    } catch (error) {
      console.error('Erreur lors du chargement des packs:', error);
    }
  };

  const applyFilters = () => {
    let filtered = [...subscribers];

    // Filtre par localisation
    if (filters.location) {
      filtered = filtered.filter(sub => 
        sub.location.toLowerCase().includes(filters.location.toLowerCase())
      );
    }

    // Filtre par type de pack
    if (filters.packType) {
      filtered = filtered.filter(sub => 
        sub.packType.toLowerCase().includes(filters.packType.toLowerCase())
      );
    }

    // Filtre par date de début
    if (filters.startDate) {
      const startDate = new Date(filters.startDate);
      filtered = filtered.filter(sub => 
        new Date(sub.saleDate) >= startDate
      );
    }

    // Filtre par date de fin
    if (filters.endDate) {
      const endDate = new Date(filters.endDate);
      endDate.setHours(23, 59, 59, 999);
      filtered = filtered.filter(sub => 
        new Date(sub.saleDate) <= endDate
      );
    }

    // Filtre par recherche
    if (filters.searchTerm) {
      const searchLower = filters.searchTerm.toLowerCase();
      filtered = filtered.filter(sub =>
        sub.clientName.toLowerCase().includes(searchLower) ||
        sub.clientEmail.toLowerCase().includes(searchLower) ||
        sub.cardNumber.includes(searchLower) ||
        sub.phoneNumber.includes(searchLower)
      );
    }

    setFilteredSubscribers(filtered);
  };

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const clearFilters = () => {
    setFilters({
      location: '',
      packType: '',
      startDate: '',
      endDate: '',
      searchTerm: ''
    });
  };

  const exportToCSV = () => {
    const headers = [
      'Numéro Carte',
      'Nom Client',
      'Email',
      'Téléphone',
      'Pack',
      'Prix (DT)',
      'Localisation',
      'Date Achat',
      'Date Expiration',
      'Période Validité',
      'Commercial',
      'Nb Enfants',
      'Noms Enfants'
    ];

    const csvData = filteredSubscribers.map(sub => [
      sub.cardNumber,
      `"${sub.clientName}"`,
      `"${sub.clientEmail}"`,
      `"${sub.phoneNumber}"`,
      `"${sub.packType}"`,
      sub.price,
      `"${sub.location}"`,
      new Date(sub.saleDate).toLocaleDateString('fr-FR'),
      new Date(sub.validityDate).toLocaleDateString('fr-FR'),
      sub.validityPeriod,
      `"${sub.salesPerson}"`,
      sub.childrenCount,
      `"${sub.childrenNames.join(', ')}"`
    ]);

    const csvContent = [
      headers.join(','),
      ...csvData.map(row => row.join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `abonnes_${new Date().toISOString().split('T')[0]}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const getStatus = (validityDate) => {
    const today = new Date();
    const expiry = new Date(validityDate);
    const diffTime = expiry - today;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

    if (diffDays < 0) return { text: 'Expiré', class: 'expired' };
    if (diffDays <= 30) return { text: 'Bientôt expiré', class: 'expiring' };
    return { text: 'Actif', class: 'active' };
  };

  const getStats = () => {
    const total = filteredSubscribers.length;
    const active = filteredSubscribers.filter(sub => 
      new Date(sub.validityDate) >= new Date()
    ).length;
    const expired = filteredSubscribers.filter(sub => 
      new Date(sub.validityDate) < new Date()
    ).length;

    return { total, active, expired };
  };

  const stats = getStats();

  return (
    <div className="subscribers-management-container">
      <div className="management-header">
        <h1 className="management-title">📋 Gestion des Abonnés</h1>
        <div className="statistics-grid">
          <div className="stat-card total-card">
            <span className="stat-number">{stats.total}</span>
            <span className="stat-label">Total Abonnés</span>
          </div>
          <div className="stat-card active-card">
            <span className="stat-number">{stats.active}</span>
            <span className="stat-label">Abonnés Actifs</span>
          </div>
          <div className="stat-card expired-card">
            <span className="stat-number">{stats.expired}</span>
            <span className="stat-label">Abonnés Expirés</span>
          </div>
        </div>
      </div>

      {/* Section Filtres */}
      <div className="filters-panel">
        <div className="filters-grid-layout">
          <div className="filter-group">
            <label className="filter-label">Recherche Globale</label>
            <input
              type="text"
              name="searchTerm"
              value={filters.searchTerm}
              onChange={handleFilterChange}
              placeholder="Nom, email, numéro de carte..."
              className="filter-input-field"
            />
          </div>

          <div className="filter-group">
            <label className="filter-label">Localisation</label>
            <select
              name="location"
              value={filters.location}
              onChange={handleFilterChange}
              className="filter-select-field"
            >
              <option value="">Toutes les localisations</option>
              {locations.map((loc, index) => (
                <option key={index} value={loc.name}>
                  {loc.name}
                </option>
              ))}
            </select>
          </div>

          <div className="filter-group">
            <label className="filter-label">Type de Pack</label>
            <select
              name="packType"
              value={filters.packType}
              onChange={handleFilterChange}
              className="filter-select-field"
            >
              <option value="">Tous les packs</option>
              {packs.map((pack, index) => (
                <option key={index} value={pack.name}>
                  {pack.name}
                </option>
              ))}
            </select>
          </div>

          <div className="filter-group">
            <label className="filter-label">Date de Début</label>
            <input
              type="date"
              name="startDate"
              value={filters.startDate}
              onChange={handleFilterChange}
              className="filter-input-field"
            />
          </div>

          <div className="filter-group">
            <label className="filter-label">Date de Fin</label>
            <input
              type="date"
              name="endDate"
              value={filters.endDate}
              onChange={handleFilterChange}
              className="filter-input-field"
            />
          </div>
        </div>

        <div className="filter-actions-panel">
          <button onClick={clearFilters} className="action-button clear-button">
            🗑️ Effacer les Filtres
          </button>
          <button onClick={exportToCSV} className="action-button export-button">
            📊 Exporter en CSV
          </button>
        </div>
      </div>

      {/* Liste des Abonnés */}
      <div className="subscribers-list-container">
        {loading ? (
          <div className="loading-state">Chargement des abonnés...</div>
        ) : filteredSubscribers.length === 0 ? (
          <div className="empty-state">Aucun abonné trouvé avec les critères sélectionnés</div>
        ) : (
          <div className="table-wrapper">
            <table className="subscribers-data-table">
              <thead>
                <tr>
                  <th>Numéro Carte</th>
                  <th>Informations Client</th>
                  <th>Coordonnées</th>
                  <th>Pack Souscrit</th>
                  <th>Montant</th>
                  <th>Point de Vente</th>
                  <th>Date Souscription</th>
                  <th>Date Expiration</th>
                  <th>Statut</th>
                  <th>Vendeur</th>
                  <th>Enfants Inclus</th>
                </tr>
              </thead>
              <tbody>
                {filteredSubscribers.map((subscriber) => {
                  const status = getStatus(subscriber.validityDate);
                  return (
                    <tr key={subscriber._id} className="data-row">
                      <td className="card-number-cell">{subscriber.cardNumber}</td>
                      <td>
                        <div className="client-details">
                          <strong className="client-name">{subscriber.clientName}</strong>
                        </div>
                      </td>
                      <td>
                        <div className="contact-details">
                          <div className="email-address">{subscriber.clientEmail}</div>
                          <div className="phone-number">{subscriber.phoneNumber}</div>
                        </div>
                      </td>
                      <td className="pack-type">{subscriber.packType}</td>
                      <td className="price-amount">{subscriber.price} DT</td>
                      <td className="location-name">{subscriber.location}</td>
                      <td className="purchase-date">
                        {new Date(subscriber.saleDate).toLocaleDateString('fr-FR')}
                      </td>
                      <td className="expiry-date">
                        {new Date(subscriber.validityDate).toLocaleDateString('fr-FR')}
                      </td>
                      <td>
                        <span className={`status-indicator ${status.class}`}>
                          {status.text}
                        </span>
                      </td>
                      <td className="sales-agent">{subscriber.salesPerson}</td>
                      <td>
                        {subscriber.childrenCount > 0 ? (
                          <div className="children-list">
                            <span className="children-count">
                              {subscriber.childrenCount} enfant(s)
                            </span>
                            {subscriber.childrenNames.map((name, index) => (
                              <div key={index} className="child-name-tag">
                                {name}
                              </div>
                            ))}
                          </div>
                        ) : (
                          <span className="no-children">Aucun</span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default SubscribersList;